// apps/web/src/pages/vendas/detalhe/vincular-material-dialog.tsx — Fase 12, Plano 07 (PV-01, D-05, T-12-22).
// Vincula o material interno ao par cliente + código do item: POST /api/v1/vendas/vinculos (pv:editar).
// O vínculo é permanente (versionado e auditado no servidor) e vale para os próximos pedidos.
import { useEffect, useState } from 'react';
import type { ConsultaVinculoPv, MaterialLookupPv } from '@pieltec/contracts';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { FormMessage } from '../../../components/ui/form-message';
import { Label } from '../../../components/ui/label';
import { MaterialCombobox } from '../../../components/vendas/material-combobox';
import { apiMutate } from '../../../lib/csrf-fetch';
import { lerErroApi } from '../../../lib/vendas/erro-api';

interface VincularMaterialDialogProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  readonly clienteId: string;
  readonly clienteNome: string;
  readonly codigoItemCliente: string;
  /** Chamado após o vínculo gravado, com o material e as sugestões devolvidas pela API. */
  readonly onVinculado: (resultado: ConsultaVinculoPv & { material: MaterialLookupPv }) => void;
}

export function VincularMaterialDialog({
  aberto,
  onAbertoChange,
  clienteId,
  clienteNome,
  codigoItemCliente,
  onVinculado,
}: VincularMaterialDialogProps) {
  const [material, setMaterial] = useState<MaterialLookupPv | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    setMaterial(null);
    setErro(null);
  }, [aberto]);

  async function vincular(): Promise<void> {
    if (!material) {
      setErro('Selecione o material interno.');
      return;
    }
    setErro(null);
    setEnviando(true);
    try {
      const res = await apiMutate('/api/v1/vendas/vinculos', {
        method: 'POST',
        body: { clienteId, codigoItemCliente: codigoItemCliente.trim(), materialId: material.id },
      });
      if (!res.ok) {
        setErro((await lerErroApi(res, 'Não foi possível vincular o material. Tente novamente.')).mensagem);
        return;
      }
      const resultado = (await res.json()) as ConsultaVinculoPv;
      onVinculado({ ...resultado, material: resultado.material ?? material });
      onAbertoChange(false);
    } catch {
      setErro('Não foi possível vincular o material. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={onAbertoChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Vincular material ao código {codigoItemCliente}</DialogTitle>
          <DialogDescription>
            O vínculo vale para {clienteNome} + código {codigoItemCliente} nos próximos pedidos.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="vinculo-material">Material interno</Label>
          <MaterialCombobox id="vinculo-material" value={material} onChange={setMaterial} invalido={!!erro && !material} />
          {erro && <FormMessage>{erro}</FormMessage>}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onAbertoChange(false)} disabled={enviando}>
            Voltar
          </Button>
          <Button type="button" onClick={() => void vincular()} disabled={enviando}>
            Vincular material
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
